/**
 * la-life-events.js — content loader for the Life Event and Life Event Topic pages.
 *
 * It renders the same cards as the life-journey filter (via window.LACards) but has
 * NONE of la-filter's filter UI — content is driven purely by the page's taxonomy.
 *
 * Two auto-initialised modes, discovered by data attributes in the DOM:
 *
 *   ROW mode  — elements with [data-la-le-row]. One row per sub-topic on a Life Event
 *               page. Fetches the top N (default 3) items for that sub-topic and renders
 *               a preview grid. "See more" (a Liquid link) points at the sub-topic page.
 *
 *   FLAT mode — an element with [data-la-le-flat]. The Life Event Topic page: every
 *               article/offering under one sub-topic, paginated behind a "Load more".
 *
 * Data attributes:
 *   [data-la-le-row]  : data-sub-topic-id, data-life-event-id (optional), data-limit=3
 *   [data-la-le-flat] : data-sub-topic-id, data-life-event-id (optional), data-page-size=12
 *
 * Both call POST {proxy}/clients/filter with { content_type, sub_topic_ids, life_event_ids }.
 */
(function () {
  "use strict";

  var CONTENT_TYPES = ["articles", "products"];

  function proxyUrl(path) {
    if (window.ENV_CONFIG && typeof window.ENV_CONFIG.getProxyUrl === "function") {
      return window.ENV_CONFIG.getProxyUrl(path);
    }
    return "/apps" + path;
  }

  // Normalise a metaobject id to a full GID. On this store `{{ metaobject.system.id }}`
  // renders as the bare numeric id (e.g. "220234121281"), but the filter API runs
  // validate_shopify_gid() on sub_topic_ids/life_event_ids and 400s on anything that
  // isn't "gid://shopify/Metaobject/…". la-filter does this same normalisation.
  function toGid(id) {
    if (!id) return id;
    id = String(id).trim();
    return id.indexOf("gid://") === 0 ? id : "gid://shopify/Metaobject/" + id;
  }

  function idList(value) {
    if (!value) return [];
    return String(value)
      .split(",")
      .map(function (s) { return s.trim(); })
      .filter(Boolean)
      .map(toGid);
  }

  function debugOn() {
    return /[?&]la_debug=1/.test(window.location.search);
  }

  // Mirrors la-filter's fetchFromAPI: same endpoint (via getProxyUrl), same headers,
  // no `credentials` (the proxy adds logged_in_customer_id server-side), response.ok
  // check, then status===0. Adds sub_topic_ids for taxonomy scoping.
  async function fetchContent(opts) {
    var body = { content_type: CONTENT_TYPES };
    if (opts.subTopicIds && opts.subTopicIds.length) body.sub_topic_ids = opts.subTopicIds;
    if (opts.lifeEventIds && opts.lifeEventIds.length) body.life_event_ids = opts.lifeEventIds;
    if (typeof opts.limit === "number") body.limit = opts.limit;
    if (typeof opts.offset === "number") body.offset = opts.offset;
    if (opts.sortField) body.sort_field = opts.sortField;

    var endpoint = proxyUrl("/clients/filter");
    var res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error("HTTP " + res.status + " from " + endpoint);
    }
    var data;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error("Non-JSON response from " + endpoint + " (HTTP " + res.status + ")");
    }
    if (!data || data.status !== 0 || !data.response) {
      var msg = (data && data.response && data.response.error) || "status " + (data && data.status);
      throw new Error("Filter error: " + msg);
    }
    return data.response; // { results, total_count, type_counts, offset, limit }
  }

  function debugLine(text) {
    return '<p class="la-le-debug" style="grid-column:1/-1;font:12px/1.5 monospace;color:#8a4b00;background:#fff6e5;border:1px solid #f0d9a8;border-radius:8px;padding:8px 10px;margin:0;word-break:break-all;">' + text + "</p>";
  }

  // ---- Placeholder resolution (parity with la-filter) ----------------------
  function isValidImg(u) {
    return typeof u === "string" && u.trim().length > 0;
  }

  function bareId(id) {
    if (!id) return id;
    return String(id).replace("gid://shopify/Metaobject/", "");
  }

  function categoryMap() {
    var map = {};
    (window.LA_LE_CATEGORIES || []).forEach(function (c) { map[c.id] = c; });
    return map;
  }

  // An item's L1 category title, or null — mirrors la-filter's getL1Parent logic.
  function l1TitleForItem(item) {
    var raw = item.category && item.category.id ? item.category.id : item.category;
    if (!raw) return null;
    var map = categoryMap();
    var cat = map[bareId(raw)];
    if (!cat) return null;
    var l1 = cat.level === "l1" ? cat : (cat.parent ? map[cat.parent] : null);
    return l1 ? l1.title : null;
  }

  // The placeholder image to use for an item with no image (null = let LACards
  // fall back to its built-in SVG). Category-based for articles, else defaults.
  function placeholderFor(item) {
    var cfg = window.LA_LE_PLACEHOLDERS || {};
    var type = item.content_type || (item.price ? "offering" : "article");
    if (type === "product" || type === "offering") {
      return cfg.offeringPlaceholder || null;
    }
    var title = l1TitleForItem(item);
    if (title && cfg.category1Name && title === cfg.category1Name && cfg.category1ArticlePlaceholder) {
      return cfg.category1ArticlePlaceholder;
    }
    if (title && cfg.category2Name && title === cfg.category2Name && cfg.category2ArticlePlaceholder) {
      return cfg.category2ArticlePlaceholder;
    }
    return cfg.articlePlaceholder || null;
  }

  // Relativize an absolute Shopify URL to a path so links honour the connected custom
  // domain instead of the *.myshopify.com host the filter API returns in item.url.
  function toRelativeUrl(url) {
    if (!url || url === "#") return url || "#";
    try {
      var u = new URL(url, window.location.origin);
      return (u.pathname || "/") + u.search + u.hash;
    } catch (e) {
      return url;
    }
  }

  // Participant/company logo, matched from window.participantsProfiles by the article's
  // author name — same logic la-filter uses (normalized + singular/plural fallback).
  var _normProfiles;
  function normName(s) {
    return String(s == null ? "" : s).toLowerCase().replace(/[‘’'"]/g, "").replace(/\s+/g, " ").trim();
  }
  function normalizedProfiles() {
    if (_normProfiles) return _normProfiles;
    _normProfiles = {};
    var p = window.participantsProfiles || {};
    Object.keys(p).forEach(function (k) { _normProfiles[normName(k)] = p[k]; });
    return _normProfiles;
  }
  function participantLogoFor(item) {
    var type = item.content_type || (item.price ? "offering" : "article");
    if (type !== "article") return null; // company badge is on article cards only
    var author = item.author || "";
    if (!author) return null;
    var profiles = window.participantsProfiles || {};
    var np = normalizedProfiles();
    var na = normName(author);
    var profile = profiles[author] || np[na];
    if (!profile && na.charAt(na.length - 1) === "s") profile = np[na.slice(0, -1)];
    if (!profile && na.charAt(na.length - 1) !== "s") profile = np[na + "s"];
    return profile ? profile.company_logo : null;
  }

  function cardOpts(item) {
    var opts = {};
    // Real image wins; otherwise use the configured placeholder as the image.
    if (!isValidImg(item.preview_image_url) && !isValidImg(item.featured_image) && !isValidImg(item.image_url)) {
      var ph = placeholderFor(item);
      if (ph) opts.imageUrl = ph;
    }
    // Article <img onerror> fallback also honours the placeholder config.
    var type = item.content_type || (item.price ? "offering" : "article");
    if (type !== "product" && type !== "offering") {
      var af = placeholderFor(item);
      if (af) opts.imageFallback = af;
    }
    // Participant/company logo (article cards), matched from participantsProfiles.
    var logo = participantLogoFor(item);
    if (logo) opts.authorLogo = logo;
    return opts;
  }

  function renderCardsHtml(items) {
    if (!window.LACards || typeof window.LACards.render !== "function") return "";
    return items
      .map(function (item) {
        item.url = toRelativeUrl(item.url); // honour the connected domain, not the myshopify host
        return window.LACards.render(item, cardOpts(item));
      })
      .join("");
  }

  // ---- Favorites (parity with la-filter's heart button) --------------------
  async function toggleFavorite(btn) {
    var id = btn.dataset.id;
    var wasFav = btn.classList.contains("is-favorited");
    var keyname = (btn.dataset.type === "offering" || (id && id.indexOf("Product") !== -1)) ? "product_id" : "article_id";
    var payload = wasFav
      ? { remove: true }
      : {
          article_title: btn.dataset.title,
          article_url: btn.dataset.url,
          image_url: btn.dataset.image,
          object_type: btn.dataset.type || "article",
        };
    payload[keyname] = id;

    // Optimistic toggle; revert on failure.
    btn.classList.toggle("is-favorited");
    btn.setAttribute("aria-pressed", wasFav ? "false" : "true");
    try {
      var res = await fetch(proxyUrl("/clients/favorites"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      var data = await res.json();
      var ok = data && (data.status === 0 || data.success === true || !data.error);
      if (!ok) throw new Error("favorite failed");
    } catch (err) {
      btn.classList.toggle("is-favorited");
      btn.setAttribute("aria-pressed", wasFav ? "true" : "false");
    }
  }

  function bindFavorites(root) {
    root.addEventListener("click", function (e) {
      var btn = e.target.closest(".favorite-btn");
      if (btn && root.contains(btn)) {
        e.preventDefault();
        toggleFavorite(btn);
      }
    });
  }

  // ---- ROW mode ------------------------------------------------------------
  async function initRow(row) {
    var cards = row.querySelector("[data-la-le-cards]");
    if (!cards) return;
    var limit = parseInt(row.dataset.limit, 10);
    if (!limit || limit < 1) limit = 3;
    var subTopicIds = idList(row.dataset.subTopicId);

    try {
      // Filter by sub-topic ONLY. The backend ANDs life_event_ids with sub_topic_ids
      // and does not reconcile a sub-topic to its parent, so also sending
      // life_event_ids would drop any item whose mirrored `life_event` field isn't a
      // literal match. The sub-topic already scopes the content to this event.
      var resp = await fetchContent({ subTopicIds: subTopicIds, limit: limit, offset: 0 });
      var items = resp.results || [];
      var tc = resp.type_counts || {};
      var dbg = "sub_topic=" + subTopicIds.join(",") + " · limit=" + limit +
        " · total_count=" + (resp.total_count || 0) +
        " · articles=" + (tc.articles || 0) + " products=" + (tc.products || 0) +
        " · showing=" + items.length;
      if (items.length === 0) {
        row.classList.add("is-empty");
        cards.innerHTML =
          (debugOn() ? debugLine("OK · " + dbg + " · 0 results (data, not code)") : "") +
          '<p class="la-le-empty">More coming soon.</p>';
        var moreLink = row.querySelector("[data-la-le-more]");
        if (moreLink) moreLink.style.display = "none";
        return;
      }
      cards.innerHTML =
        (debugOn() ? debugLine("OK · " + dbg) : "") +
        renderCardsHtml(items);
      // "See more" shows when the sub-topic has 3 or more items; hidden at fewer than 3.
      var moreLink = row.querySelector("[data-la-le-more]");
      if (moreLink && (resp.total_count || 0) < 3) moreLink.style.display = "none";
    } catch (err) {
      row.classList.add("is-error");
      cards.innerHTML =
        (debugOn() ? debugLine("FAIL · sub_topic=" + subTopicIds.join(",") + " · " + (err && err.message)) : "") +
        '<p class="la-le-empty">Content is unavailable right now.</p>';
      console.error("[la-life-events] row load failed", subTopicIds, err);
    }
  }

  // ---- FLAT mode -----------------------------------------------------------
  function initFlat(flat) {
    var cards = flat.querySelector("[data-la-le-cards]");
    if (!cards) return;
    var moreBtn = flat.querySelector("[data-la-le-loadmore]");
    var countEl = flat.querySelector("[data-la-le-count]");
    var initialCount = parseInt(flat.dataset.initial, 10);
    if (!initialCount || initialCount < 1) initialCount = 12;
    var loadMoreCount = parseInt(flat.dataset.loadMore, 10);
    if (!loadMoreCount || loadMoreCount < 1) loadMoreCount = 6;
    var moreLabel = (moreBtn && moreBtn.dataset.label) || "Load more";

    var subTopicIds = idList(flat.dataset.subTopicId);
    var offset = 0;
    var total = null;
    var loading = false;
    var firstLoad = true;

    async function loadPage() {
      if (loading) return;
      loading = true;
      // Initial page loads `initialCount` (12); each Load more adds `loadMoreCount` (6).
      var lim = firstLoad ? initialCount : loadMoreCount;
      if (moreBtn) {
        moreBtn.disabled = true;
        if (!firstLoad) moreBtn.textContent = "Loading…";
      }
      try {
        var resp = await fetchContent({
          subTopicIds: subTopicIds,
          limit: lim,
          offset: offset,
        });
        var items = resp.results || [];
        total = typeof resp.total_count === "number" ? resp.total_count : offset + items.length;

        if (offset === 0 && items.length === 0) {
          flat.classList.add("is-empty");
          cards.innerHTML =
            (debugOn() ? debugLine("OK · sub_topic=" + subTopicIds.join(",") + " · total_count=" + (resp.total_count || 0) + " · 0 results (data, not code)") : "") +
            '<p class="la-le-empty">No content here yet.</p>';
        } else {
          if (offset === 0 && debugOn()) cards.innerHTML = debugLine("OK · sub_topic=" + subTopicIds.join(",") + " · total_count=" + resp.total_count);
          cards.insertAdjacentHTML("beforeend", renderCardsHtml(items));
        }
        offset += items.length;
        firstLoad = false;

        if (countEl) countEl.textContent = total === 1 ? "1 result" : total + " results";
        if (moreBtn) {
          moreBtn.textContent = moreLabel;
          if (offset >= total || items.length === 0) {
            moreBtn.style.display = "none";
          } else {
            moreBtn.disabled = false;
          }
        }
      } catch (err) {
        flat.classList.add("is-error");
        if (offset === 0) {
          cards.innerHTML =
            (debugOn() ? debugLine("FAIL · sub_topic=" + subTopicIds.join(",") + " · " + (err && err.message)) : "") +
            '<p class="la-le-empty">Content is unavailable right now.</p>';
        }
        if (moreBtn) { moreBtn.textContent = moreLabel; moreBtn.disabled = false; }
        console.error("[la-life-events] flat load failed", subTopicIds, err);
      } finally {
        loading = false;
      }
    }

    if (moreBtn) moreBtn.addEventListener("click", loadPage);
    loadPage();
  }

  function init() {
    var rows = document.querySelectorAll("[data-la-le-row]");
    rows.forEach(initRow);

    var flat = document.querySelector("[data-la-le-flat]");
    if (flat) initFlat(flat);

    // Favorite toggling on any life-event content container.
    document.querySelectorAll("[data-la-le-root]").forEach(bindFavorites);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
